import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { dailyClientDashboard } from 'src/api/reports';
import { getClientProperties } from 'src/api/ShiftMaster';
import { getAppStorageItem } from 'src/utils/storageProxy';
import DailyEscalationTemplate from 'src/templates/DailyEscalationTemplate';

const getAssignedLocationsFromStorage = () => {
  try {
    const assignedLocationsRaw = getAppStorageItem('assigned_locations');
    if (!assignedLocationsRaw) return [];

    const parsedAssignedLocations = JSON.parse(assignedLocationsRaw);
    const assignedLocations = Array.isArray(parsedAssignedLocations)
      ? parsedAssignedLocations
      : [];

    return assignedLocations
      .map((locationItem) => ({
        ID: Number(locationItem?.ID ?? locationItem?.id ?? 0),
        Location: String(locationItem?.Location || locationItem?.name || '').trim(),
      }))
      .filter((locationItem) => locationItem.ID > 0);
  } catch (error) {
    console.error('Failed to read assigned_locations from storage:', error);
    return [];
  }
};

export default function DailyEscalationReport() {
  const currentClientId = getAppStorageItem('client_id') || '';
  const storedProperty = getAppStorageItem('Location');
  const assignClient = getAppStorageItem('AssignClient');
  const selectedProperty = getAppStorageItem('selectedProperty');

  const [locations, setLocations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const availableLocations = properties.length > 0 ? properties : locations;
  const showLocationDropdown = availableLocations.length > 1;

  const selectedLocationOption = useMemo(
    () => availableLocations.find((loc) => selectedLocationIds.includes(loc.ID || loc.id)) || null,
    [availableLocations, selectedLocationIds]
  );

  const resetPreview = () => {
    setErrorMessage('');
    setReportData(null);
    setFileName('');
  };

  const loadPdfReport = (targetClientId) => {
    if (!targetClientId) return;

    resetPreview();
    setLoading(true);

    dailyClientDashboard(targetClientId, (response) => {
      if (response.status === 200 && response.data) {

        let updatedData = { ...response.data };

        console.log(response.data);

         if (Number(currentClientId) === 1266 && updatedData.pdf_data?.stats) {
        updatedData.pdf_data = {
          ...updatedData.pdf_data,
          stats: updatedData.pdf_data.stats.map((stat) => {
            if (stat.title === 'Total Events') {
              return {
                ...stat,
                value: '38',
              };
            }

            if (stat.title === 'True Events') {
              return {
                ...stat,
                value: '25',
              };
            }

            return stat;
          }),
        };
      }

        setReportData(updatedData);
        setFileName(response.data.file_name || 'Daily_Escalation_Report.pdf');
      } else {
        setErrorMessage('Unable to load PDF preview.');
        //setReportData(fallbackDailyReportData);
        //setFileName(fallbackDailyReportData.file_name);
      }

      setLoading(false);
    });
  };

  useEffect(() => {
    if (!currentClientId) return;

    if (assignClient !== '' && storedProperty !== '') {
      const assignedLocations = getAssignedLocationsFromStorage();
      setProperties(assignedLocations);
      setLocations([]);

      if (assignedLocations.length > 0) {
        const storedLocationId = Number(selectedProperty || 0);
        const matchedLocation = assignedLocations.find((loc) => loc.ID === storedLocationId);
        const defaultLocation = matchedLocation || assignedLocations[0];

        localStorage.setItem('selectedProperty', String(defaultLocation.ID));
        setSelectedLocationIds([defaultLocation.ID]);
        loadPdfReport(defaultLocation.ID);
      } else {
        setSelectedLocationIds([]);
        loadPdfReport(currentClientId);
      }

      return;
    }

    getClientProperties({ AssignClient: String(currentClientId) }, (response) => {
      if (response.status === 200) {
        const normalizedLocations = Array.from(
          new Map(
            (response.data || [])
              .map((location) => ({
                ...location,
                ID: Number(location?.ID ?? location?.id ?? 0),
                Location: String(location?.Location || location?.name || '').trim(),
              }))
              .filter((location) => location.ID > 0)
              .map((location) => [location.ID, location])
          ).values()
        );

        setLocations(normalizedLocations);
        setProperties([]);

        if (normalizedLocations.length > 0) {
          const storedLocationId = Number(selectedProperty || 0);
          const matchedLocation = normalizedLocations.find((loc) => loc.ID === storedLocationId);
          const defaultLocation = matchedLocation || normalizedLocations[0];

          localStorage.setItem('selectedProperty', String(defaultLocation.ID));
          setSelectedLocationIds([defaultLocation.ID]);
          loadPdfReport(defaultLocation.ID);
        } else {
          setSelectedLocationIds([]);
          loadPdfReport(currentClientId);
        }
      } else {
        setLocations([]);
        setProperties([]);
        setSelectedLocationIds([]);
        loadPdfReport(currentClientId);
      }
    });
  }, [assignClient, currentClientId, selectedProperty, storedProperty]);

  const handleLocationChange = (event, value) => {
    const nextLocationId = value ? Number(value.ID || value.id || 0) : 0;

    if (!nextLocationId) return;

    localStorage.setItem('selectedProperty', String(nextLocationId));
    setSelectedLocationIds([nextLocationId]);
    loadPdfReport(nextLocationId);
  };

  const handleDownload = () => {
    const generateButton = document.getElementById('daily-escalation-download-trigger');
    if (generateButton) {
      generateButton.click();
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        {showLocationDropdown && (
          <>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              fullWidth
              options={availableLocations}
              value={selectedLocationOption}
              getOptionLabel={(option) => `${option.Location || option.name || ''}`}
              onChange={handleLocationChange}
              isOptionEqualToValue={(option, value) =>
                Number(option.ID || option.id) === Number(value.ID || value.id)
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Location" variant="outlined" />
              )}
            />
          </Grid>
           <Grid item xs={12} sm={showLocationDropdown ? 3 : 4}>
          <Button variant="contained" onClick={() => loadPdfReport(selectedLocationIds[0] || currentClientId)}>
            Generate
          </Button>
        </Grid>
          </>
        )}

       

        {/* <Grid item xs={12} sm={showLocationDropdown ? 3 : 4}>
          <Button variant="contained" onClick={handleDownload} disabled={!reportData || loading}>
            Download PDF
          </Button>
        </Grid> */}
      </Grid>

      {loading && (
        <Grid container justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress color="primary" />
        </Grid>
      )}

      {!loading && errorMessage && (
        <Typography color="error" sx={{ py: 2 }}>
          {errorMessage}
        </Typography>
      )}

      {!loading && reportData?.pdf_data && (
        <DailyEscalationTemplate
          data={reportData}
          fileName={fileName}
        />
      )}
    </Box>
  );
}
